import { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { setHttpMessage } from 'store/messageSlice'
import ApiService from 'services/api.service'

import FormUdEjecutora from './components/FormUdEjecutora'
import ViewUdEjecutora from './components/ViewUdEjecutora'
import Dialog, { ConfirmDialog } from 'components/Dialog'
import Button, { ButtonIcon } from 'components/Button'
import TitlePage from 'components/TitlePage'
import useFilters from 'hooks/useFilters'
import DataTable from 'components/DataTable'
import { initialPagination } from 'data/const'
import InputCheckbox from 'components/InputCheckbox'

/** @module Pages/UnidadEjecutora/UnidadEjecutoraIndex */

// Opciones de filtros para el hook useFilters.
const filterOptions = {contain: ['codigo', 'codigo_da', 'descripcion'], bool: ['activo']}
// Opciones de las unidades ejecutoras.
const actions = [
    {icon: 'bi-eye', title: 'Información', action: 'view'},
    {icon: 'bi-pencil', title: 'Editar', action: 'update'},
    {icon: 'bi-trash', title: 'Eliminar', action: 'delete'},
]

/**
 * Página de administración de Unidades Ejecutoras.
 * @returns {JSX.Element} Retorna el componente UnidadEjecutoraIndex.
 */
function UnidadEjecutoraIndex() {
    const dispatch = useDispatch()
    const [udEjecutoras, setUdEjecutoras] = useState([])
    const [pagination, setPagination] = useState(initialPagination)
    const {filters, reloadFilters, handleFilter, handlePage, handleSort} = useFilters({initialFilters: null, filterOptions})
    const [selectedUdEjecutora, setSelectedUdEjecutora] = useState(null)
    const [show, setShow] = useState({form: false, delete: false, info: false})
    const [submitting, setSubmitting] = useState(false)
    const tableRef = useRef()
    const formRef = useRef()
    
    useEffect(()=>{
        const getUdEjecutoras = async () =>{
            const response = await ApiService.udEjecutora.index(filters)
            if (response.status === 200) {
                setUdEjecutoras(response.data.data)
                setPagination(response.data.pagination)
            }
        }
        if (filters) getUdEjecutoras()
    }, [filters])

    // Controlador para abrir los modales
    const handleOpenDialog = (action, rowData) => {
        setSelectedUdEjecutora(rowData)
        if (action === 'update' || action === 'create') {
            openDialog('form')
        } else if (action === 'delete') {
            openDialog('delete')
        } else if (action === 'view') {
            openDialog('info')
        }
    }
    const openDialog = (name) => setShow({...show, [name]: true})
    const closeFormDialog = () => setShow({...show, form: false})
    const closeDeleteDialog = () => setShow({...show, delete: false})
    const closeInfoDialog = () => setShow({...show, info: false})

    const handleSubmit = () => formRef?.current?.handleSubmit()
    const handleCreate = async (values, formikBag) => {
        setSubmitting(true)
        const response = await ApiService.udEjecutora.create(values)
        dispatch(setHttpMessage({status: response.status, title: response.data.message}))
        if (response.status === 201) {
            reloadFilters()
            closeFormDialog()
        } else if (response.status === 422) {
            const errors = response.data.errors
            const _errors = {}
            for (const key in errors) {
                _errors[key] = errors[key][0]
            }
            formikBag.setErrors(_errors)
        }
        setSubmitting(false)
    }
    const handleUpdate = async (values, formikBag) => {
        if (selectedUdEjecutora) {
            setSubmitting(true)
            const response = await ApiService.udEjecutora.update(values, selectedUdEjecutora.id)
            dispatch(setHttpMessage({status: response.status, title: response.data.message}))
            if (response.status === 200) {
                reloadFilters()
                closeFormDialog()
            } else if (response.status === 422) {
                const errors = response.data.errors
                const _errors = {}
                for (const key in errors) {
                    _errors[key] = errors[key][0]
                }
                formikBag.setErrors(_errors)
            }
            setSubmitting(false)
        }
    }
    const handleDelete = async () => {
        if (selectedUdEjecutora) {
            const response = await ApiService.udEjecutora.delete(selectedUdEjecutora.id)
            dispatch(setHttpMessage({status: response.status, title: response.data.message}))
            if (response.status === 200) {
                reloadFilters()
                closeDeleteDialog()
            }
        }
    }
    const clearSelected = () => setSelectedUdEjecutora(null)
    const handleClean = () => tableRef?.current?.clearFilters()

    const actionsBodyTemplate = (rowData) => {
        const _options = actions.reduce((carry, option) => {
            if ((typeof option.condition === 'function' && option.condition(rowData)) || typeof option.condition !== 'function') {
                carry.push(
                    <ButtonIcon 
                        variant='text-secondary' rounded
                        icon={option.icon} title={option.title} key={option.title} 
                        onClick={() => handleOpenDialog(option.action, rowData)}
                    />
                )
            }
            return carry
        }, [])
        return <div>{_options}</div>
    }
    const columns = [
        {
            field: 'codigo_da',
            header: 'DA',
            filter: true,
            sortable: true,
            style: { width: '10rem' },
        },
        {
            field: 'codigo',
            header: 'Código',
            filter: true,
            sortable: true,
            style: { width: '10rem' },
        },
        {
            field: 'descripcion',
            header: 'Nombre',
            filter: true,
            sortable: true,
        },
        {
            field: 'gestion',
            header: 'Gestión',
            sortable: true,
            bodyClassName: 'text-center',
        },
        {
            field: 'activo',
            header: 'Activo',
            body: (rowData) => rowData.activo ? 'Si' : 'No',
            filter: true,
            sortable: true,
            bodyClassName: 'text-center',
            filterElement: (options) => (
                <div className='d-flex justify-content-center'>
                    <InputCheckbox value={options.value} onChange={(e) => options.filterCallback(e.value)} triState style={{fontSize: '1.5rem'}}/>
                </div>
            ),
        },
        {
            field: 'id',
            header: 'Opciones',
            filter: true,
            filterElement: () => <div className='d-flex justify-content-center'><Button startIcon='pi pi-filter' onClick={handleClean} >Limpiar</Button></div>,
            body: actionsBodyTemplate,
            style: { width: '10rem' },
        },
    ]
    const formFooterDialog = <>
        <Button variant='text-secondary' startIcon='pi pi-times' onClick={closeFormDialog}>Cancelar</Button>
        <Button 
            variant='text-secondary' startIcon='pi pi-save' 
            type='submit' onClick={handleSubmit}
            loading={submitting}
        >Guardar</Button>
    </>
    const infoFooterDialog = <Button variant='text-secondary' startIcon='pi pi-times' onClick={closeInfoDialog}>Cerrar</Button>
    const options = <>
        <Button startIcon='pi pi-plus' onClick={()=>handleOpenDialog('create', null)} >Nuevo</Button>
    </>

    return <>
        <TitlePage title='Lista de Unidades Ejecutoras' options={options} />
        <div className='content'>
            <div>
                <DataTable 
                    innerRef={tableRef}
                    values={udEjecutoras} 
                    columns={columns} 
                    filterable
                    onFilter={handleFilter}
                    onSort={handleSort}
                    pagination
                    rowsPerPage={pagination.pageSize}
                    totalRecords={pagination.totalRecords}
                    page={pagination.currentPage}
                    onPage={handlePage}
                />

                <Dialog
                    show={show.form}   
                    header={selectedUdEjecutora ? 'Editar UE' : 'Registrar UE'} 
                    footer={formFooterDialog}
                    keyboard={false}
                    style={{ width: '30rem' }}
                    onHide={closeFormDialog}
                    onExited={clearSelected}
                >
                    <FormUdEjecutora
                        formRef={formRef} selectedUdEjecutora={selectedUdEjecutora}
                        onSubmit={ selectedUdEjecutora ? handleUpdate : handleCreate }  
                    />
                </Dialog>
                <ConfirmDialog
                    show={show.delete}
                    onHide={closeDeleteDialog}
                    onReject={closeDeleteDialog}
                    onAccept={handleDelete}
                    onExited={clearSelected}
                >
                    Esta seguro de <span className='text-primary'>ELIMINAR</span> la Unidad Ejecutora "{selectedUdEjecutora?.descripcion}"?
                </ConfirmDialog>
                <Dialog
                    show={show.info}   
                    header='Información de Unidad Ejecutora' 
                    footer={infoFooterDialog}
                    onHide={closeInfoDialog}
                    style={{ width: '35rem' }}
                    onExited={clearSelected}
                >
                    {selectedUdEjecutora ? <ViewUdEjecutora selectedUdEjecutora={selectedUdEjecutora} /> : null}
                </Dialog>

            </div>
        </div>
    </>
}
export default UnidadEjecutoraIndex